import React from 'react';
import { IStore } from '../../../../../redux/store';
import { ConnectedProps, connect } from 'react-redux';
import moment from 'moment';
import { inspectorSelectors } from 'redux/app/vessel/inspector';
import { vesselSelectors } from 'redux/app/vessel';

type OwnProps = {
    // isPrintVisible: boolean;
	vesselId: string;
};
type Annex6CoverPageProps = OwnProps & PropsFromRedux;

function IntroductionPage() {
    return (
        <div className="newpage flex flex-col justify-between py-4 px-8 mb-2 text-justify">
            <h2 className="font-medium text-2xl">Introduction</h2>
            <p className="my-4 mx-0">
                This section shows a comprehensive list of items of on board
                operations which are, when required, to be recorded in the
                MARPOL VI Record Book in accordance with regulations 12, 13, 14
                and 18 of Annex VI of the International Convention for the
                Prevention of Pollution from Ships, 1973, as amended (MARPOL
                Annex VI) and accompanying IMO resolutions and circulars. The
                items have been grouped into operational sections, each of which
                is denoted by a letter Code.
            </p>
            <p className="my-4 mx-0">
                When making entries in the MARPOL VI Record Book the date,
                operational Code and item numeric code shall be recorded in the
                appropriate Columns and the required particulars shall be
                recorded chronologically. Each completed operation shall be
                signed for and dated by the engine officer, or officers, in
                charge of completion of a specific operation or part thereof.
                The Chief Engineer Officer shall sign where appropriate to
                confirm correctness of performed operation. The Master of the
                ship shall sign each completed page.
            </p>
            <p className="my-4 mx-0">
                Any failure of the installed on board emission abatement
                technology, including SOx scrubbers (Exhaust Gas Cleaning
                Systems (EGCS), NOx scrubbers (Selective Catalytic Reduction
                (SCR)) or other systems), exhaust gas recirculation systems
                (EGR) or other equipment of similar purpose shall be noted in
                the MARPOL VI Record Book. A failure shall be considered an
                inability of the above listed equipment to maintain correct
                required emission parameters for more than 1 hour due to any
                reason.
            </p>
            <p className="my-4 mx-0">
                Where this MARPOL VI Record Book is used on board a flag ship
                all entries shall be in English.
            </p>
            <p className="my-4 mx-0">
                The MARPOL VI Record Book shall be kept in such a place as to be
                readily available for inspection at all reasonable times and,
                except in the case of unmanned ships under tow, shall be kept on
                board the ship. It shall be preserved on board the ship for a
                period of three years after the last entry has been made.
            </p>
            <p className="my-4 mx-0">
                The competent authority of the Government of a Party to the
                Convention may inspect the MARPOL VI Record Book on board any
                ship to which Annex VI applies while the ship is in port,
                offshore terminal or waters under their jurisdiction and may
                make a copy of any entry in that book and may require the Master
                of the ship to certify that the copy is a true copy of such
                entry. Any copy so made which has been certified by the Master
                of the ship as a true copy of an entry in the MARPOL VI Record
                Book shall be made admissible in any juridical proceedings as
                evidence of the facts stated in the entry. The inspection of the
                MARPOL VI Record Book and, where requested, handling of a
                certified copy of an entry by the competent authority under this
                paragraph shall be performed as expeditiously as possible
                without causing the ship to be unduly delayed.
            </p>
        </div>
    );
}

function ItemsTobeRecorded() {
    return (
        <div className="newpage mt-10 py-0 px-4">
            <h3 className="text-xl font-medium mb-6">
                LIST OF ITEMS TO BE RECORDED
            </h3>
            <ol className="list-alpha ml-4" type="A">
                <li className="mb-8">
                    <b>Bunkering of Low Sulphur fuel oil</b>
                    <ol className="list-decimal ml-4">
                        <li className="my-2 mx-0">
                            <span>Bunkering</span>
                            <ol className="list-decimal ml-4">
                                <li className="my-2 mx-0">
                                    Place or position of bunkering.
                                </li>
                                <li className="my-2 mx-0">
                                    Time of bunkering.
                                </li>
                                <li className="my-2 mx-0">
                                    Fuel type and sulphur content (% m/m)
                                </li>
                                <li className="my-2 mx-0">
                                    Quantity of fuel oil and identity of
                                    tank(-s) (state quantity added, in tonnes
                                    and total content of tank(s)).
                                </li>
                                <li className="my-2 mx-0">
                                    Bunker Delivery Note (BDN) Reference No.
                                </li>
                            </ol>
                        </li>
                        <li className="my-2 mx-0">
                            <span>
                                De-bunkering of Low Sulphur fuel oil<sup>1</sup>
                            </span>
                            <ol className="list-decimal ml-4">
                                <li className="my-2 mx-0">
                                    Place or position of de-bunkering.
                                </li>
                                <li className="my-2 mx-0">
                                    Time of de-bunkering.
                                </li>
                                <li className="my-2 mx-0">
                                    Bunker Delivery Note (BDN) Reference No. in
                                    relation to original supply of the
                                    de-bunkered fuel oil
                                </li>
                                <li className="my-2 mx-0">
                                    Quantity of fuel oil and identity of
                                    tank(-s) (state quantity de-bunkered, in
                                    tonnes and quantity retained in tank(s)).
                                </li>
                            </ol>
                        </li>
                    </ol>
                </li>

                <li className="mb-8">
                    <b>Tracking and control of MARPOL fuel oil samples.</b>
                    <p>
                        This record shall be produced on the date the MARPOL
                        sample has been received on board and the date the
                        MARPOL sample has been disposed of in relation to all
                        types of bunkered fuel oil.
                    </p>
                    <ol className="list-decimal ml-4" start={3}>
                        <li className="my-2 mx-0">
                            Bunker Delivery Note (BDN) Reference No.
                        </li>
                        <li className="my-2 mx-0">
                            <span>MARPOL Sample:</span>
                            <ol className="list-decimal ml-4">
                                <li className="my-2 mx-0">
                                    Received MARPOL sample seal number(s).
                                </li>
                                <li className="my-2 mx-0">
                                    Date(s) MARPOL sample disposed of
                                    <sup>2</sup>
                                </li>
                            </ol>
                        </li>
                    </ol>
                </li>

                <div className="py-0 px-5 mb-6 mt-32">
                    <div
                        className=" border-[#2D3748] border-[0.5px]  mt-4 mb-3 mx-0"
                        style={{ width: 224 }}
                    />
                    <div className="my-2 mx-0 flex items-start">
                        <sup className="text-xs mr-1.5 mt-2">1</sup>
                        <span className="text-sm">
                            The ship's master should obtain from the operator of
                            the reception facilities, which includes barges and
                            tank trucks, a receipt or certificate detailing the
                            quantity of low sulphur fuel oil debunkered,
                            together with the time and date of the transfer. The
                            receipt or certificate should be kept together with
                            the MARPOL VI Record Book.
                        </span>
                    </div>
                    <div className="my-2 mx-0 flex items-start">
                        <sup className="text-xs mr-1.5 mt-2">2</sup>
                        <span className="text-sm">
                            MARPOL samples shall be retained under the ship's
                            control until the fuel oil is substantially
                            consumed, but in any case, for a period of not less
                            than 12 months from the date of delivery.
                        </span>
                    </div>
                </div>
            </ol>
            <ol className="newpage list-alpha ml-4" start={3}>
                <li className="mb-8 mt-12">
                    <b>Transfer of custody of MARPOL sample</b>
                    <ol className="list-decimal ml-4" start={5}>
                        <li className="my-2 mx-0">
                            Bunker Delivery Note (BDN) Reference No. and the
                            date the associated fuel oil was originally received
                            on board
                        </li>
                        <li className="my-2 mx-0">
                            <span>MARPOL Sample:</span>
                            <ol className="list-decimal ml-4">
                                <li className="my-2 mx-0">
                                    MARPOL sample seal number(s).
                                </li>
                                <li className="my-2 mx-0">
                                    Place or position of custody transfer.
                                </li>
                                <li className="my-2 mx-0">
                                    Receiver address and contact details
                                    <sup>3</sup>
                                </li>
                            </ol>
                        </li>
                    </ol>
                </li>

                <li className="mb-8">
                    <b>
                        Low sulphur fuel changeover record for Sulphur Emission
                        Control Area (SECA).
                    </b>
                    <ol className="list-decimal ml-4" start={7}>
                        <li className="my-2 mx-0">
                            <span>Changeover on entry to SECA.</span>
                            <ol className="list-decimal ml-4">
                                <li className="my-2 mx-0">
                                    Time and position at start of changeover to
                                    low sulphur fuel. Duty Engineer Officer name
                                    and signature.
                                </li>
                                <li className="my-2 mx-0">
                                    Time and position on completion of
                                    changeover to low sulphur fuel. Duty
                                    Engineer Officer name and signature.
                                </li>
                                <li className="my-2 mx-0">
                                    Quantity of low sulphur fuel and identity of
                                    tank(-s) at the end of changeover (state
                                    quantity, in tonnes, and total content of
                                    tank(s)).
                                </li>
                                <li className="my-2 mx-0">
                                    Time and position at entry into SECA. Duty
                                    Engineer Officer and Chief Engineer Officer
                                    name and signature.
                                </li>
                            </ol>
                        </li>
                        <li className="my-2 mx-0">
                            <span>Changeover on exit from SECA.</span>
                            <ol className="list-decimal ml-4">
                                <li className="my-2 mx-0">
                                    Time and position at start of changeover to
                                    non-low sulphur fuel. Duty Engineer Officer
                                    name and signature.
                                </li>
                                <li className="my-2 mx-0">
                                    Time and position on completion of
                                    changeover to non-low sulphur fuel. Duty
                                    Engineer Officer name and signature.
                                </li>
                                <li className="my-2 mx-0">
                                    Quantity of low sulphur fuel and identity of
                                    tank(s) (state quantity, in tonnes, and
                                    total content of tank(s)).
                                </li>
                                <li className="my-2 mx-0">
                                    Time and position on exit from ECA. Duty
                                    Engineer Officer and Chief Engineer Officer
                                    name and signature.
                                </li>
                            </ol>
                        </li>
                    </ol>
                </li>
            </ol>

            <div className="py-0 px-5 mb-2 mt-20">
                <div
                    className=" border-[#2D3748] border-[0.5px]  mt-4 mb-3 mx-0"
                    style={{ width: 224 }}
                />
                <div className="my-2 mx-0 flex items-start">
                    <sup className="text-xs mr-1.5 mt-2">3</sup>
                    <span className="text-sm">
                        The Master should obtain a receipt from the receiver of
                        the sample, which should be kept on board until not less
                        than 12 months from the date the associated fuel oil was
                        originally received on board.
                    </span>
                </div>
            </div>
            <ol start={5} className="newpage list-alpha ml-4 pt-8">
                <li className=" mb-8 ">
                    <b>
                        Exhaust gas cleaning system (EGCS) change of settings
                        for Sulphur Emission Control Area (SECA) entry and exit
                    </b>
                    <ol className="list-decimal ml-4" start={9}>
                        <li className="my-2 mx-0">
                            <span>Change of settings on entry to SECA.</span>
                            <ol className="list-decimal ml-4">
                                <li className="my-2 mx-0">
                                    Time and position at start of settings
                                    change.
                                </li>
                                <li className="my-2 mx-0">
                                    Time and position on completion of settings
                                    change.
                                </li>
                                <li className="my-2 mx-0">
                                    Time and position on entry into SECA. Duty
                                    Engineer Officer and Chief Engineer Officer
                                    name and signature.
                                </li>
                            </ol>
                        </li>
                        <li className="my-2 mx-0">
                            <span>Change of settings on exit from SECA.</span>
                            <ol className="list-decimal ml-4">
                                <li className="my-2 mx-0">
                                    Time and position at start of settings
                                    change.
                                </li>
                                <li className="my-2 mx-0">
                                    Time and position on completion of settings
                                    change.
                                </li>
                                <li className="my-2 mx-0">
                                    Time and position on exit from SECA. Duty
                                    Engineer Officer and Chief Engineer Officer
                                    name and signature.
                                </li>
                            </ol>
                        </li>
                    </ol>
                </li>

                <li className="mb-8">
                    <b>
                        Records of NOx Emission Control Area (NECA) entry and
                        exit<sup>4</sup>
                    </b>
                    <ol className="list-decimal ml-4" start={11}>
                        <li className="my-2 mx-0">
                            <span>Entry to NECA</span>
                            <ol className="list-decimal ml-4">
                                <li className="my-2 mx-0">
                                    Time and position at entry to NECA (Tier
                                    III) area.
                                </li>
                                <li className="my-2 mx-0">
                                    Status on/off of all engines<sup>5</sup> and
                                    their SCR/EGR equipment. List all engines
                                    and their Tier here.
                                </li>
                            </ol>
                        </li>
                        <li className="my-2 mx-0">
                            <span>Exit from NECA</span>
                            <ol className="list-decimal ml-4">
                                <li className="my-2 mx-0">
                                    Time and position at exit from NECA (Tier
                                    III) area.
                                </li>
                                <li className="my-2 mx-0">
                                    Status on/off of all engines<sup>5</sup> and
                                    their SCR/EGR equipment. List all engines
                                    and their Tier here.
                                </li>
                            </ol>
                        </li>
                        <li className="my-2 mx-0">
                            <span>
                                Engine start and/or stop whilst in NECA (Tier
                                III) area.
                            </span>
                            <ol className="list-decimal ml-4">
                                <li className="my-2 mx-0">
                                    Time and position of engine<sup>5</sup>{' '}
                                    start. Record engine and its Tier here
                                </li>
                                <li className="my-2 mx-0">
                                    Time and position of engine<sup>5</sup>{' '}
                                    stop. Record engine and its Tier here.
                                </li>
                            </ol>
                        </li>
                    </ol>
                </li>
            </ol>

            <div className="py-0 px-5 mb-6 mt-24">
                <div
                    className=" border-[#2D3748] border-[0.5px]  mt-4 mb-3 mx-0"
                    style={{ width: 224 }}
                />
                <div className="my-2 mx-0 flex items-start">
                    <sup className="text-xs mr-1.5 mt-2">4</sup>
                    <span className="text-sm">
                        As required by Regulation 13.5.3 of MARPOL Anex VI
                    </span>
                </div>
                <div className="my-2 mx-0 flex items-start">
                    <sup className="text-xs mr-1.5 mt-2">5</sup>
                    <span className="text-sm">
                        For all engines installed no board to which Regulation
                        13.5.1 applies
                    </span>
                </div>
            </div>

            <ol className="newpage list-alpha ml-4" start={7}>
                <li className="my-8">
                    <b>
                        Records of EGR bleed off water operations<sup>6</sup>
                    </b>
                    <ol className="list-decimal ml-4" start={14}>
                        <li className="my-2 mx-0">
                            <span>
                                EGR bleed off water holding tank (s) inventory.
                            </span>
                            <p>
                                Content should be recorded weekly (this means
                                the content should be recorded once a week even
                                if the voyage lasts more than one week).
                            </p>
                            <ol className="list-decimal ml-4">
                                <li className="my-2 mx-0">
                                    identity of tank(s)
                                </li>
                                <li className="my-2 mx-0">
                                    capacity of tank(s) in m<sup>3</sup>.
                                </li>
                                <li className="my-2 mx-0">
                                    content of tank(s) in m<sup>3</sup>.
                                </li>
                            </ol>
                        </li>
                        <li className="my-2 mx-0">
                            <span>
                                EGR bleed of water holding tank(s) discharge
                            </span>
                            <ol className="list-decimal ml-4">
                                <li className="my-2 mx-0">
                                    To reception facilities<sup>7</sup>. State
                                    port/location and time of start and stop of
                                    discharge and total quantity discharged for
                                    each holding tank in m<sup>3</sup>.
                                </li>
                                <li className="my-2 mx-0">
                                    Overboard in compliance with section 3.1.2
                                    of MEPC.307(73). State time and position of
                                    start and stop of discharge and total
                                    quantity discharged for each holding tank in
                                    m<sup>3</sup>.
                                </li>
                                <li className="my-2 mx-0">
                                    Overboard in compliance with section 3.2.3
                                    of MEPC.307(73). State time and position of
                                    start and stop of discharge and total
                                    quantity discharged for each holding tank in
                                    m<sup>3</sup>.
                                </li>
                            </ol>
                        </li>
                        <li className="my-2 mx-0">
                            <span>
                                EGR water treatment system residues storage and
                                discharge to reception facilities <sup>8</sup>
                            </span>
                            <ol className="list-decimal ml-4">
                                <li className="my-2 mx-0">
                                    Residues transfer to storage tank. State
                                    identity of storage tank and quantity
                                    transferred in m<sup>3</sup>.
                                </li>
                                <li className="my-2 mx-0">
                                    Residues discharge to reception facilities
                                    <sup>7</sup>, State port/location, time of
                                    start and stop of discharge and total
                                    quantity discharged in m<sup>3</sup>.
                                </li>
                            </ol>
                        </li>
                    </ol>
                </li>

                <li className="mb-8">
                    <b>
                        Records of failure of installed SOx/NOx Emission
                        Abatement systems <sup>9</sup>
                    </b>
                    <ol className="list-decimal ml-4" start={17}>
                        <li className="my-2 mx-0">
                            Time and position when system failure occurred.
                            State system identification and fuel consumers
                            affected.
                        </li>
                        <li className="my-2 mx-0">
                            Time and position when system operation restored.
                            State system identification and fuel consumers
                            affected.
                        </li>
                    </ol>
                </li>
            </ol>

            <div className="py-0 px-5 mb-6">
                <div
                    className=" border-[#2D3748] border-[0.5px]  mt-4 mb-3 mx-0"
                    style={{ width: 224 }}
                />
                <div className="my-2 mx-0 flex items-start">
                    <sup className="text-xs mr-1.5 mt-2">6</sup>
                    <span className="text-sm">
                        In accordance with MEPC.307(73)
                    </span>
                </div>
                <div className="my-2 mx-0 flex items-start">
                    <sup className="text-xs mr-1.5 mt-2">7</sup>
                    <span className="text-sm">
                        The ship's master should obtain from the operator of the
                        reception facilities, which includes barges and tank
                        trucks, a receipt or certificate detailing the quantity
                        of bleed off water or residues transferred, together
                        with the time and date of the transfer. This receipt or
                        certificate, if atached to the MARPOL VI Record Book may
                        aid the master of the ship in proving that his or her
                        ship was not involved in an alleged polution incident.
                        The receipt or certificate should be kept together with
                        the MARPOL VI Record Book.
                    </span>
                </div>
                <div className="my-2 mx-0 flex items-start">
                    <sup className="text-xs mr-1.5 mt-2">8</sup>
                    <span className="text-sm">
                        In accordance with Section 4 of MEPC.307(73)
                    </span>
                </div>
                <div className="my-2 mx-0 flex items-start">
                    <sup className="text-xs mr-1.5 mt-2">9</sup>
                    <span className="text-sm">
                        To record failure of SOx EGCS, NOx SCR/EGR or other
                        emission abatement technology installed on board.
                    </span>
                </div>
            </div>

            <ol className="newpage list-alpha ml-4" start={9}>
                <li className=" mb-8 pt-4">
                    <b>Ozone depleting substances (ODS)</b>
                    <ol className="list-decimal ml-4" start={19}>
                        <li className="my-2 mx-0">
                            <span>Delivery of ODS:</span>
                            <ol className="list-decimal ml-4">
                                <li className="my-2 mx-0">
                                    Place or position of delivery.
                                </li>
                                <li className="my-2 mx-0">Time of delivery.</li>
                                <li className="my-2 mx-0">
                                    ODS name/type and quantity received (in
                                    kilograms)
                                </li>
                            </ol>
                        </li>
                        <li className="my-2 mx-0">
                            <span>
                                Disposal of ODS to shore reception facility
                                <sup>10</sup>
                            </span>
                            <ol className="list-decimal ml-4">
                                <li className="my-2 mx-0">
                                    Place or position of disposal.
                                </li>
                                <li className="my-2 mx-0">Time of disposal.</li>
                                <li className="my-2 mx-0">
                                    ODS name/type and quantity disposed (in
                                    kilograms)
                                </li>
                            </ol>
                        </li>
                        <li className="my-2 mx-0">
                            <span>Record of ODS use:</span>
                            <ol className="list-decimal ml-4">
                                <li className="my-2 mx-0">Time.</li>
                                <li className="my-2 mx-0">
                                    Description of repair or maintenance carried
                                    out.
                                </li>
                                <li className="my-2 mx-0">
                                    Name of system charged and total capacity of
                                    ODS in the system (in kilograms).
                                </li>
                                <li className="my-2 mx-0">
                                    ODS name/type and quantity charged (in
                                    kilograms).
                                </li>
                            </ol>
                        </li>
                        <li className="my-2 mx-0">
                            <span>
                                Discharge or escape of ODS to atmosphere:
                            </span>
                            <ol className="list-decimal ml-4">
                                <li className="my-2 mx-0">
                                    Place or position of ship at time of
                                    occurrence.
                                </li>
                                <li className="my-2 mx-0">Time.</li>
                                <li className="my-2 mx-0">
                                    ODS name/type and quantity discharged (in
                                    kilograms).
                                </li>
                                <li className="my-2 mx-0">
                                    Circumstances of discharge or escape, the
                                    reasons therefore and general remarks.
                                </li>
                            </ol>
                        </li>
                    </ol>
                </li>

                <li className="mb-8">
                    <b>Additional operational procedures and general remarks</b>
                </li>
                <div
                    className="py-0 px-5 mb-6 mt-20"
                    style={{ marginLeft: -24 }}
                >
                    <div
                        className=" border-[#2D3748] border-[0.5px]  mt-4 mb-3 mx-0"
                        style={{ width: 224 }}
                    />
                    <div className="my-2 mx-0 flex items-start">
                        <sup className="text-xs mr-1.5 mt-2">10</sup>
                        <span className="text-sm">
                            The ship's master should obtain from the operator of
                            the reception facilities, a receipt or certificate
                            detailing the ODS name/type and quantity
                            transferred, together with the time and date of the
                            transfer. This receipt or certificate, if attached
                            to the MARPOL VI Record Book may aid the master of
                            the ship in proving that his or her ship was not
                            involved in an alleged pollution incident. The
                            receipt or certificate should be kept together with
                            the MARPOL VI Record Book.
                        </span>
                    </div>
                </div>
            </ol>
        </div>
    );
}

function Annex6CoverPage(props: Annex6CoverPageProps) {
    const { vesselName, imoNumber, dateRange, distinctiveNumberOrLetters } =
        props;
    // returns the cover page for the marpol oil record book 1
    return (
        <div>
            <div className="flex flex-col justify-between py-4 px-8 mb-2">
                <div className="font-medium text-center text-xl my-24 mt-48 mx-0">
                    <h2 className="m-4">Annex VI Record Book</h2>
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginTop: '2rem',
                        marginBottom: '10rem',
                    }}
                    className="flex justify-center mt-8 mb-40"
                >
                    <div className="w-2/3 mt-2.5">
                        <div
                            className="flex items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <p className="mr-2 min-w-fit mb-0">Name of ship:</p>
                            <p className="inline-block px-3 text-[#808080] border-b border-dashed text-sm border-[#808080] w-full mb-0">
                                {vesselName}
                            </p>
                        </div>
                        <div
                            className="flex items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <p className="mr-2 min-w-fit mb-0">
                                Distinctive number or letters:
                            </p>
                            <p className="inline-block px-3 text-[#808080] border-b border-dashed text-sm border-[#808080] w-full mb-0 self-end">
                                {distinctiveNumberOrLetters}
                            </p>
                        </div>
                        <div
                            className="flex items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <p className="mr-2 min-w-fit mb-0">IMO Number:</p>
                            <p className="inline-block px-3 text-[#808080] border-b border-dashed text-sm border-[#808080] w-full mb-0">
                                {imoNumber}
                            </p>
                        </div>

                        <div
                            style={{ fontSize: '1.125rem' }}
                            className="flex items-center mb-4"
                        >
                            <p className="mr-2 min-w-fit mb-0">
                                Gross tonnage:
                            </p>
                            <p className="inline-block px-3 text-[#808080] border-b border-dashed text-sm border-[#808080] w-full mb-0 self-end">
                                {props?.tonnage?.grossTonnage || ''}
                            </p>
                        </div>
                        <div
                            className="flex justify-between items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <div
                                style={{ width: '54%' }}
                                className="flex items-center  mb-2"
                            >
                                <p className="mr-2 min-w-fit">Period from :</p>
                                <p className="inline-block px-3 text-[#808080] border-b border-dashed border-[#808080] w-full mb-0 text-sm">
                                    {dateRange?.startDate &&
                                        moment(dateRange?.startDate).format(
                                            'DD-MM-yyyy'
                                        )}{' '}
                                </p>
                            </div>
                            <div
                                style={{ width: '45%' }}
                                className="flex items-center mb-2"
                            >
                                <p className="mr-2 min-w-fit">to :</p>
                                <p className="inline-block px-3 text-[#808080] border-b border-dashed border-[#808080] w-full mb-0 text-sm">
                                    {dateRange?.endDate &&
                                        moment(dateRange?.endDate).format(
                                            'DD-MM-yyyy'
                                        )}
                                </p>
                            </div>
                        </div>
                        <div
                            className="flex items-center mb-4"
                            style={{ fontSize: '1.125rem' }}
                        >
                            <p className="mr-2 min-w-fit mb-0">Working language of the ship:</p>
                            <p className="inline-block px-3 text-[#808080] border-b border-dashed border-[#808080] w-full mb-0 text-sm">
                                English
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div>
                <IntroductionPage />
            </div>
            <div style={{ margin: '2rem 0' }}>
                <ItemsTobeRecorded />
            </div>
        </div>
    );
}

function mapStateToProps(store: IStore, ownProps: OwnProps) {
    const inspectorStore = inspectorSelectors._getStore(store);

    const dateRange = inspectorSelectors.getDateRange(inspectorStore);

	const vesselStore = vesselSelectors._getStore(store);
	const vesselInfo = vesselSelectors.getVesselById(
		ownProps.vesselId,
		vesselSelectors.getVessels(vesselStore)
	);
    return {
        vesselName: vesselInfo.vesselName,
        imoNumber: vesselInfo.imoNumber,
        dateRange,
        tonnage: vesselInfo.tonnage,
        distinctiveNumberOrLetters: vesselInfo?.distinctiveNumberOrLetters,
    };
}
const reduxConnector = connect(mapStateToProps, {});
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(Annex6CoverPage);
